import { Outlet } from 'react-router';

import { apiServerFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { AcceptCookiesContainer } from '#/containers/AcceptCookiesContainer';
import { InviteAcceptedToastContainer } from '#/containers/InviteAcceptedToast/InviteAcceptedToastContainer';
import { SignInOrUpReminderContainer } from '#/containers/SignInOrUpReminder/SignInOrUpReminderContainer';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { combinedHandleElements } from '#/horizon/utils/combined-handle-elements';
import { combinedLoaderHandle } from '#/horizon/utils/combined-loader-handle';

import type { Route } from './+types/Layout';
import { LayoutHeader } from './Header/LayoutHeader';
import {
  LayoutBaseVariantProgressBarHolder,
  LayoutChildrenHolder,
  LayoutHolder,
  LayoutMobileSearchPageFooterPadding,
} from './Layout.styled';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from './LayoutHandleProps';
import { PageFooter } from './PageFooter/PageFooter';

export async function loader({ request, url, context }: Route.LoaderArgs) {
  const { user } = await apiServerFetchUserEnvironment({
    request,
    context,
  })();

  const { serverContext } = getServerRouterContext(context);

  return {
    user,
    url: {
      pathname: url.pathname,
      search: url.search,
    },
    locale: serverContext.locale,
    hidePtSunsetBanner: serverContext.hidePtSunsetBanner,
  };
}

export function ServerComponent({
  matches,
  loaderData: { user, url, locale, hidePtSunsetBanner },
}: Route.ServerComponentProps) {
  const {
    darkBackground,
    hideFooterDivider: hideFooterDividerHandle,
    isSearchPage = false,
    hideMobileHeaderOnScroll = false,
    variant = 'default',
    urlByLocale,
    fallbackUrlByLocaleToCurrent,
    defaultSearchType = 'JOB',
    searchFromPageType,
    showDesktopSearch = false,
    isSearchLandingPage = false,
    showSearchRefinements = false,
    focusedShowSubmenu = false,
    focusedShowCart = false,
    focusedTitle = '',
    focusedExitLink = '',
  } = combinedLoaderHandle<LayoutHandleProps>(matches);

  const { searchRefinements, focusedSubmenu } =
    combinedHandleElements<LayoutHandleElements>(matches);

  const isLoggedOut = !user;

  const hideFooterDivider =
    hideFooterDividerHandle === 'hide-footer-divider-if-logged-out'
      ? isLoggedOut
      : Boolean(hideFooterDividerHandle);

  return (
    <LayoutHolder data-dark-background={darkBackground ? true : undefined}>
      {variant === 'base' ? (
        <LayoutBaseVariantProgressBarHolder>
          <RouteLoadingProgressBar />
        </LayoutBaseVariantProgressBarHolder>
      ) : (
        <LayoutHeader
          variant={variant}
          defaultSearchType={defaultSearchType}
          searchFromPageType={searchFromPageType}
          showDesktopSearch={showDesktopSearch}
          isSearchLandingPage={isSearchLandingPage}
          showSearchRefinements={showSearchRefinements}
          searchRefinements={searchRefinements}
          isSearchPage={isSearchPage}
          hideMobileHeaderOnScroll={hideMobileHeaderOnScroll}
          user={user}
          locale={locale}
          redirectTo={`${url.pathname}${url.search}`}
          hidePtSunsetBanner={hidePtSunsetBanner}
          focused={{
            showSubmenu: focusedShowSubmenu,
            showCart: focusedShowCart,
            submenu: focusedSubmenu,
            title: focusedTitle,
            exitLink: focusedExitLink,
          }}
        />
      )}

      <LayoutChildrenHolder data-qa-id="layout-content">
        <Outlet />
      </LayoutChildrenHolder>

      {variant === 'default' && (
        <>
          <PageFooter
            locale={locale}
            urlByLocale={
              urlByLocale ??
              (fallbackUrlByLocaleToCurrent
                ? { en: url.pathname, es: url.pathname }
                : null)
            }
            showDivider={!hideFooterDivider}
          />

          {isSearchPage && <LayoutMobileSearchPageFooterPadding />}
        </>
      )}

      {variant !== 'focused' && (
        <>
          <AcceptCookiesContainer />
          <SignInOrUpReminderContainer />
          <InviteAcceptedToastContainer />
        </>
      )}
    </LayoutHolder>
  );
}
